import superagent from 'superagent'
import queryString from 'query-string'
import { getToken, clearUserInfo } from 'utils/authLocalStorage'
import formatUrl from 'apis/formatUrl'
import { api_version } from 'config'
import moment from 'moment-timezone'
import { getWWClientId, getWWClientSecret } from 'utils/wwLocalStorage'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { getErrorPagePath } from 'utils/basic'

const methods = ['get', 'post', 'put', 'patch', 'delete']

const timezone = moment.tz.guess()

const requestCache = new Map()

function getCacheKey(path, params, version, partnerPath) {
  const url = formatUrl(path, version, partnerPath)
  const paramsStr = params ? JSON.stringify(params) : ''
  return `${url}${paramsStr}`
}

function getCachedResponse(cacheKey, cacheTtl) {
  const entry = requestCache.get(cacheKey)
  if (!entry) return null
  const isExpired = Date.now() - entry.timestamp > cacheTtl
  return isExpired ? null : entry.data
}

function setCachedResponse(cacheKey, data) {
  requestCache.set(cacheKey, { data, timestamp: Date.now() })
}

export default class ApiClient {
  constructor(req) {
    methods.forEach((method) => {
      this[method] = (
        path,
        {
          params,
          body,
          header,
          version,
          partnerPath,
          failCb,
          cacheTtl,
          forceRefresh = false,
        } = {}
      ) => {
        const executeRequest = () => {
          const request = superagent[method](
            formatUrl(path, version, partnerPath)
          )
          if (params) {
            request.query(params)
          }

          if (header) {
            for (let i in header) {
              request.set(i, header[i])
            }
          }
          const token = getToken()
          request.set('Authorization', `Bearer ${token}`)
          request.set('app_version', api_version)
          request.set('platform', 'MobileWeb')
          request.set('timezone', timezone)
          request.set('client-id', getWWClientId() || '')
          request.set('client-secret', getWWClientSecret() || '')

          if (body) {
            request.send(body)
          }

          return new Promise((resolve, reject) => {
            request.end((err, { body, text } = {}) => {
              if (err) {
                logAmplitudeEvent('Error: Network Error', {
                  'Request URL': formatUrl(path, version, partnerPath),
                  'Error Code': (body && body.code) || err.status || '502',
                  'Error Description': (body && body.msg) || err.message || '',
                  'Error Response': body || err,
                  'Status Code': (body && body.code) || err.status || '502',
                })
                failCb && failCb(body)
                if (
                  body &&
                  body.code === 401 &&
                  path.indexOf('/user/login') === -1
                ) {
                  let { r } = queryString.parse(window.location.search)
                  if (!r) {
                    r = window.location.pathname
                  }
                  r = r[0] === '/' ? r : '/' + r
                  clearUserInfo()
                  return (window.location.href = getErrorPagePath())
                }
              }
              const result = body || text || err
              if (method === 'get' && cacheTtl > 0 && result && !err) {
                const cacheKey = getCacheKey(path, params, version, partnerPath)
                setCachedResponse(cacheKey, result)
              }
              resolve(result)
            })
          })
        }

        if (method === 'get' && cacheTtl > 0 && !forceRefresh) {
          const cacheKey = getCacheKey(path, params, version, partnerPath)
          const cached = getCachedResponse(cacheKey, cacheTtl)
          if (cached !== null) {
            return Promise.resolve(cached)
          }
        }

        return executeRequest()
      }
    })
  }

  static clearCache(pathPrefix) {
    if (!pathPrefix) {
      requestCache.clear()
      return
    }
    for (const key of requestCache.keys()) {
      if (key.includes(pathPrefix)) {
        requestCache.delete(key)
      }
    }
  }

  empty() {}
}
